import { sum } from 'ramda';
import type { Editor, NodeEntry } from 'slate';

import { arrayEntries, convertUnit, maxValueOf, sumOf, twipsToPx } from '../../util';
import type {
  BaseRenderModelTable,
  BaseRenderModelTableCell,
  BaseRenderModelTableCellProps,
  BaseRenderModelTableRow,
  RenderModelTable,
  RenderModelTableCell,
  RenderModelTableRow,
  TableCellElement,
  TableElement,
  TableRowElement,
} from '../internal';
import { getOrCreateCachedRMBlockChild, isRenderModelContentBlock, Queries } from '../internal';

export const DEFAULT_COLUMN_WIDTH_TWIPS = 360;
export const MIN_ROW_HEIGHT_TWIPS = 240;

export function buildRenderModelTable(
  editor: Editor,
  options: {
    containerWidthPx: number;
    isInHeaderFooter: boolean;
    pageNum: number;
    tableEntry: NodeEntry<TableElement>;
  }
): RenderModelTable {
  const { containerWidthPx, isInHeaderFooter, pageNum, tableEntry } = options;
  const [table, tablePath] = tableEntry;
  const cachedKey = Queries.findKey(table);
  const align = Queries.renderedTableAlign(table);

  const columnWidthsPx = Queries.renderedTableColumnWidths(table, {
    availableWidth: convertUnit(containerWidthPx, 'px', 'tw'),
  }).map(twipsToPx);

  const children = table.children.map((tableRow, rowIndex) =>
    buildRenderModelTableRow(editor, {
      columnWidthsPx,
      isInHeaderFooter,
      nextTableRow: table.children.at(rowIndex + 1) ?? null,
      pageNum,
      previousTableRow: rowIndex > 0 ? (table.children.at(rowIndex - 1) ?? null) : null,
      table,
      tableRowEntry: [tableRow, [...tablePath, rowIndex]],
    })
  );

  const widthPx = maxValueOf(children, 'widthPx', 0);

  const alignOffsetPx: number = {
    center: (containerWidthPx - widthPx) / 2,
    left: Queries.renderedTableIndent(table, 'px'),
    right: containerWidthPx - widthPx,
  }[align];

  return rMTableWithDerrivedProperties({
    ...table,
    alignOffsetPx,
    children,
    columnWidthsPx,
    key: cachedKey.id,
    relativePath: Queries.getPathRelativeToParent(tablePath),
    widthPx,
  });
}

function buildRenderModelTableRow(
  editor: Editor,
  options: {
    columnWidthsPx: number[];
    isInHeaderFooter: boolean;
    nextTableRow: TableRowElement | null;
    pageNum: number;
    previousTableRow: TableRowElement | null;
    table: TableElement;
    tableRowEntry: NodeEntry<TableRowElement>;
  }
): BaseRenderModelTableRow {
  const {
    columnWidthsPx,
    isInHeaderFooter,
    nextTableRow,
    pageNum,
    previousTableRow,
    table,
    tableRowEntry,
  } = options;
  const [tableRow, tableRowPath] = tableRowEntry;

  let startColumnIndex = 0;

  const children = tableRow.children.map((tableCell, cellIndex) => {
    const endColumnIndex = Queries.getTableCellEndColumnFromStartColumn(
      tableCell,
      startColumnIndex
    );
    const widthPx = sum(columnWidthsPx.slice(startColumnIndex, endColumnIndex + 1));

    const rMTableCell = buildRenderModelTableCell(editor, {
      isInHeaderFooter,
      nextTableCell: tableRow.children.at(cellIndex + 1) ?? null,
      nextTableRow,
      pageNum,
      previousTableCell: cellIndex > 0 ? (tableRow.children.at(cellIndex - 1) ?? null) : null,
      previousTableRow,
      startColumnIndex,
      table,
      tableCellEntry: [tableCell, [...tableRowPath, cellIndex]],
      widthPx,
    });

    startColumnIndex = endColumnIndex + 1;
    return rMTableCell;
  });

  const firstCell = children.at(0);
  const leftmostBorderWidthPx = firstCell
    ? Queries.renderedTableBorderWidth(firstCell.renderedBorders.left, 'px')
    : 0;

  const lastCell = children.at(-1);
  const rightmostBorderWidthPx = lastCell
    ? Queries.renderedTableBorderWidth(lastCell.renderedBorders.right, 'px')
    : 0;

  return {
    ...tableRow,
    children,
    key: Queries.findKey(tableRow).id,
    relativePath: Queries.getPathRelativeToParent(tableRowPath),
    widthPx: sumOf(children, 'widthPx') + (leftmostBorderWidthPx + rightmostBorderWidthPx) / 2,
  };
}

function buildRenderModelTableCell(
  editor: Editor,
  options: {
    isInHeaderFooter: boolean;
    nextTableCell: TableCellElement | null;
    nextTableRow: TableRowElement | null;
    pageNum: number;
    previousTableCell: TableCellElement | null;
    previousTableRow: TableRowElement | null;
    startColumnIndex: number;
    table: TableElement;
    tableCellEntry: NodeEntry<TableCellElement>;
    widthPx: number;
  }
): BaseRenderModelTableCell {
  const {
    isInHeaderFooter,
    nextTableCell,
    nextTableRow,
    pageNum,
    previousTableCell,
    previousTableRow,
    startColumnIndex,
    table,
    tableCellEntry,
    widthPx,
  } = options;
  const [tableCell, tableCellPath] = tableCellEntry;
  const cachedKey = Queries.findKey(tableCell);
  const endColumnIndex = Queries.getTableCellEndColumnFromStartColumn(tableCell, startColumnIndex);

  // Get the table cells touching this cell on its top and bottom edges
  const cellsAbove = Queries.getTableCellsInColumnRange(previousTableRow, {
    endColumnIndex,
    startColumnIndex,
  });

  const cellsBelow = Queries.getTableCellsInColumnRange(nextTableRow, {
    endColumnIndex,
    startColumnIndex,
  });

  const props: BaseRenderModelTableCellProps = {
    ...tableCell,
    endColumnIndex,
    key: cachedKey.id,
    relativePath: Queries.getPathRelativeToParent(tableCellPath),
    renderedBorders: {
      bottom: cellsBelow.map(cellBelow =>
        Queries.renderedTableCellBorder(table, tableCell, cellBelow, 'bottom')
      ),
      left: Queries.renderedTableCellBorder(table, tableCell, previousTableCell, 'left'),
      right: Queries.renderedTableCellBorder(table, tableCell, nextTableCell, 'right'),
      top: cellsAbove.map(cellAbove =>
        Queries.renderedTableCellBorder(table, tableCell, cellAbove, 'top')
      ),
    },
    startColumnIndex,
    widthPx,
  };

  const contentWidthPx = Queries.getRMTableCellContentWidthPx(table, props);

  const children = tableCell.children.map((tableCellChild, index) =>
    getOrCreateCachedRMBlockChild(editor, {
      containerWidthPx: contentWidthPx,
      isInHeaderFooter,
      nodeEntry: [tableCellChild, [...tableCellPath, index]],
      pageNum,
    })
  );

  return { ...props, children };
}

/**
 * Compute all derived properties for a base render model table.
 */
export function rMTableWithDerrivedProperties(baseRMTable: BaseRenderModelTable): RenderModelTable {
  const tableChildren: RenderModelTableRow[] = [];

  function lookBackForVMCells(columnIndex: number, rowIndex: number): RenderModelTableCell[] {
    const prevRowIndex = rowIndex - 1;
    if (prevRowIndex < 0) return [];

    const prevRow = tableChildren.at(prevRowIndex);
    if (!prevRow) throw new Error('Previous row does not exist');

    const prevCell = prevRow.children.find(
      ({ startColumnIndex }) => startColumnIndex === columnIndex
    );
    if (!prevCell) return [];

    switch (Queries.renderedTableCellVerticalMerge(prevCell)) {
      case undefined:
        return [];

      case 'restart':
        return [prevCell];

      case 'continue':
        return [...lookBackForVMCells(columnIndex, prevRowIndex), prevCell];
    }
  }

  function lookAheadForVMCell(columnIndex: number, rowIndex: number): boolean {
    const nextRow = baseRMTable.children.at(rowIndex + 1);
    if (!nextRow) return false;

    const nextCell = nextRow.children.find(
      ({ startColumnIndex }) => startColumnIndex === columnIndex
    );
    if (!nextCell) return false;

    return Queries.renderedTableCellVerticalMerge(nextCell) === 'continue';
  }

  baseRMTable.children.forEach((baseRMTableRow, rowIndex) => {
    const specifiedRowHeightPx = Queries.getTableRowSpecifiedHeight(baseRMTableRow, 'px');

    const rowChildren = baseRMTableRow.children.map((baseRMTableCell): RenderModelTableCell => {
      const verticalMerge = Queries.renderedTableCellVerticalMerge(baseRMTableCell);

      const previousVMCells =
        verticalMerge === 'continue'
          ? lookBackForVMCells(baseRMTableCell.startColumnIndex, rowIndex)
          : [];

      const firstVMCell = previousVMCells.at(0);

      // Top margin comes from the first vertically merged cell
      const marginTopPx = Queries.renderedTableCellMargin(
        baseRMTable,
        firstVMCell ?? baseRMTableCell,
        'top',
        'px'
      );

      // Bottom margin comes from the last vertically merged cell
      const marginBottomPx = Queries.renderedTableCellMargin(
        baseRMTable,
        baseRMTableCell,
        'bottom',
        'px'
      );

      const verticalMergeIsStart = previousVMCells.length === 0;
      const verticalMergeIsEnd =
        !verticalMerge || !lookAheadForVMCell(baseRMTableCell.startColumnIndex, rowIndex);

      // Content comes only from the first vertically merged cell
      const contentHeightPx =
        firstVMCell?.contentHeightPx ??
        sumOf(baseRMTableCell.children.filter(isRenderModelContentBlock), 'heightPx');

      const minHeightPx = verticalMergeIsEnd
        ? Math.max(
            0,
            contentHeightPx - sumOf(previousVMCells, 'heightPx') + marginTopPx + marginBottomPx
          )
        : 0;

      return {
        ...baseRMTableCell,
        contentHeightPx,
        heightPx: -1, // Computed below
        minHeightPx,
        verticalMergeHeightPx: -1, // Computed below
        verticalMergeIsEnd,
        verticalMergeIsStart,
      };
    });

    const maxMinHeightPx = maxValueOf(rowChildren, 'minHeightPx', twipsToPx(MIN_ROW_HEIGHT_TWIPS));
    const heightPx = specifiedRowHeightPx ?? maxMinHeightPx;

    for (const rMTableCell of rowChildren) {
      rMTableCell.heightPx = heightPx;
      rMTableCell.verticalMergeHeightPx = rMTableCell.verticalMergeIsStart ? heightPx : 0;
    }

    tableChildren.push({
      ...baseRMTableRow,
      children: rowChildren,
      heightPx,
    });
  });

  // Determine the final verticalMergeHeightPx for all vertically merged cells
  for (const [rMTableRowIndex, rMTableRow] of arrayEntries(tableChildren)) {
    const subsequentRows = tableChildren.slice(rMTableRowIndex + 1);

    for (const rMTableCell of rMTableRow.children) {
      const {
        startColumnIndex: columnIndex,
        verticalMergeIsEnd,
        verticalMergeIsStart,
      } = rMTableCell;
      if (!verticalMergeIsStart || verticalMergeIsEnd) continue;

      // Accumulate verticalMergeHeightPx for subsequent vertically merged cells
      for (const subsequentRow of subsequentRows) {
        const nextCellInColumn = subsequentRow.children.find(
          ({ startColumnIndex }) => startColumnIndex === columnIndex
        );
        if (!nextCellInColumn) break;

        rMTableCell.verticalMergeHeightPx += nextCellInColumn.heightPx;
        if (nextCellInColumn.verticalMergeIsEnd) break;
      }
    }
  }

  const totalRowHeightPx = sumOf(tableChildren, 'heightPx');

  const maxTopBorderWidthPx = Queries.getTableMaximumBorderWidth(baseRMTable, 'top', 'px');
  const maxBottomBorderWidthPx = Queries.getTableMaximumBorderWidth(baseRMTable, 'bottom', 'px');
  const heightPx = totalRowHeightPx + maxTopBorderWidthPx / 2 + maxBottomBorderWidthPx / 2;

  return {
    ...baseRMTable,
    children: tableChildren,
    heightPx,
  };
}

/**
 * Constructs a new version of the given table with certain properties modified and all derived
 * properties recomputed.
 */
export function modifyRMTable(
  baseRMTable: BaseRenderModelTable,
  props: Partial<BaseRenderModelTable>
): RenderModelTable {
  return rMTableWithDerrivedProperties({ ...baseRMTable, ...props });
}
